import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, RefreshCw, Workflow } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { navLabel } from '../utils/navigation';
import InfoTip from '../components/InfoTip';
import MonthPicker from '../components/MonthPicker';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { shiftMonth } from '../utils/urlParams';
import { usePeriodParams } from '../hooks/usePeriodParams';
import { useTabParam } from '../hooks/useTabParam';
import FlowScoreCard from '../components/flow/FlowScoreCard';
import FlowTabs from '../components/flow/FlowTabs';
import HandoffList from '../components/flow/HandoffList';
import ReworkView from '../components/flow/ReworkView';
import WaitingView from '../components/flow/WaitingView';
import { pctTone } from '../components/hygiene/types';
import { FlowSummary, HandoffPerson, HandoffRow, ReworkData } from '../components/flow/types';
import * as fmt from '../lib/format';

const PAGE = 200;
type TabId = 'handoffs' | 'rework' | 'waiting';

// Flow: who moves tickets, whether they come back, and where they wait. The checks (handoffs and
// rework) follow Hygiene's visibility; waiting time is delivery health that every role may read, so
// a leader sees the Waiting view only.
export default function FlowPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canSeeChecks = user?.role !== 'leader';

  // The month, the project, "only mine" and the tab live in the address, so a reload or a copied link opens
  // the same view (and a link from Compliance can open a month and a project).
  const { period, current, isCurrent, setPeriod, projectKey, setProjectKey, mine, setMine } = usePeriodParams();
  const [tab, setTab] = useTabParam<TabId>(canSeeChecks ? ['handoffs', 'rework', 'waiting'] : ['waiting'], canSeeChecks ? 'handoffs' : 'waiting');
  const [refreshKey, setRefreshKey] = useState(0);

  const [summary, setSummary] = useState<FlowSummary | null>(null);
  const [summaryMeta, setSummaryMeta] = useState<{ unlinked?: boolean; people_without_role?: number }>({});
  const [loadingSummary, setLoadingSummary] = useState(canSeeChecks);

  const [rows, setRows] = useState<HandoffRow[]>([]);
  const [people, setPeople] = useState<HandoffPerson[]>([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(PAGE);
  const [loadingRows, setLoadingRows] = useState(true);

  const [rework, setRework] = useState<ReworkData | null>(null);
  const [loadingRework, setLoadingRework] = useState(true);

  const [error, setError] = useState<string | null>(null);
  const useMine = !isAdmin && mine;

  // Scores per project (and the overall figure) for the month.
  useEffect(() => {
    if (!canSeeChecks) return;
    let cancelled = false;
    setLoadingSummary(true);
    apiService
      .getFlowSummary({ period, mine: useMine })
      .then((r) => {
        if (cancelled) return;
        if (r.success) {
          setSummary(r.data);
          setSummaryMeta(r.meta ?? {});
          setError(null);
        } else setError(r.error || 'Could not load the scores');
      })
      .catch((e: any) => !cancelled && setError(e?.response?.data?.error || 'Could not load the scores'))
      .finally(() => !cancelled && setLoadingSummary(false));
    return () => {
      cancelled = true;
    };
  }, [period, useMine, canSeeChecks, refreshKey]);

  useEffect(() => {
    if (!canSeeChecks || tab !== 'handoffs') return;
    let cancelled = false;
    setLoadingRows(true);
    apiService
      .getFlowHandoffs({ period, project_key: projectKey ?? undefined, mine: useMine, limit })
      .then((r) => {
        if (cancelled) return;
        if (r.success) {
          setRows(r.data ?? []);
          setPeople(r.people ?? []);
          setTotal(r.meta?.total ?? 0);
        }
      })
      .catch((e: any) => !cancelled && setError(e?.response?.data?.error || 'Could not load the moves'))
      .finally(() => !cancelled && setLoadingRows(false));
    return () => {
      cancelled = true;
    };
  }, [period, projectKey, useMine, limit, tab, canSeeChecks, refreshKey]);

  useEffect(() => {
    if (!canSeeChecks || tab !== 'rework') return;
    let cancelled = false;
    setLoadingRework(true);
    apiService
      .getFlowRework({ period, project_key: projectKey ?? undefined, mine: useMine })
      .then((r) => {
        if (cancelled) return;
        if (r.success) setRework(r.data);
      })
      .catch((e: any) => !cancelled && setError(e?.response?.data?.error || 'Could not load the tickets sent back'))
      .finally(() => !cancelled && setLoadingRework(false));
    return () => {
      cancelled = true;
    };
  }, [period, projectKey, useMine, tab, canSeeChecks, refreshKey]);

  const changePeriod = (next: string) => {
    setPeriod(next);
    setLimit(PAGE);
  };
  const changeProject = (key: string | null) => {
    setProjectKey(key);
    setLimit(PAGE);
  };

  const periodIsCurrent = isCurrent;
  const overall = summary?.overall;
  const projects = summary?.projects ?? [];
  const noRoles = !!overall && overall.checked === 0 && overall.unchecked !== undefined && overall.unchecked.no_role > 0;
  const refreshing = loadingSummary || loadingRows || loadingRework;

  const emptyHandoffs = useMemo(() => {
    if (useMine) return summaryMeta.unlinked ? 'Your login is not matched to a Jira account yet.' : `None of your moves in ${fmt.month(period)} were outside the usual owner.`;
    if (noRoles) return 'No one who moved tickets has a role yet, so no move can be judged.';
    if ((overall?.moves ?? 0) === 0) return `No ticket changed status in ${fmt.month(period)} within your scope.`;
    return `Every judged move in ${fmt.month(period)} was made by the usual owner.`;
  }, [useMine, summaryMeta.unlinked, period, noRoles, overall]);

  const tabs = canSeeChecks
    ? [
        { id: 'handoffs', label: 'Outside the usual owner', count: overall?.outside, tip: 'flow.handoffs' as const },
        { id: 'rework', label: 'Sent back', count: overall?.rework, tip: 'flow.rework' as const },
        { id: 'waiting', label: 'Waiting', tip: 'flow.waiting' as const },
      ]
    : [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100/60">
      <PageHeader title={navLabel('/flow')} subtitle="How work moves, and where it waits" icon={Workflow} />
      <div className="p-4 sm:p-6">
        <div className="mx-auto max-w-7xl">
          {/* Period and filters */}
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1">
              <button type="button" aria-label="Previous month" onClick={() => changePeriod(shiftMonth(period, -1))} className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-white hover:text-gray-900 active:scale-[0.96] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <MonthPicker value={period} max={current} onChange={changePeriod} />
              <button type="button" aria-label="Next month" disabled={periodIsCurrent} onClick={() => changePeriod(shiftMonth(period, 1))} className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-white hover:text-gray-900 active:scale-[0.96] disabled:opacity-30 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            {canSeeChecks && !isAdmin && tab !== 'waiting' && (
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input type="checkbox" checked={mine} onChange={(e) => { setMine(e.target.checked); setLimit(PAGE); }} className="rounded border-gray-300 text-blue-600 focus:ring-blue-200" />
                Only mine
              </label>
            )}
            {projectKey && (
              <button type="button" onClick={() => changeProject(null)} className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-800 ring-1 ring-inset ring-blue-500 transition-colors active:scale-[0.97] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                {projectKey}
                <span aria-hidden>×</span>
                <span className="sr-only">Clear the project filter</span>
              </button>
            )}
            <div className="ml-auto flex items-center gap-3">
              {canSeeChecks && overall && overall.checked > 0 && (
                <div className="text-sm text-gray-600">
                  <span className={`text-lg font-bold tabular-nums ${pctTone(overall.adherence_pct)}`}>{fmt.percent(overall.adherence_pct)}</span>
                  <span className="ml-1.5">by the usual owner · {fmt.count(overall.outside)} of {fmt.count(overall.checked)} moves outside</span>
                  <InfoTip id="flow.adherence" />
                </div>
              )}
              <button type="button" onClick={() => setRefreshKey((n) => n + 1)} aria-label="Refresh" className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-white hover:text-gray-900 active:scale-[0.96] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin motion-reduce:animate-none' : ''}`} />
              </button>
            </div>
          </div>

          {error && <div className="mb-4 rounded-xl bg-rose-50 px-4 py-2.5 text-sm text-rose-700 ring-1 ring-rose-200" role="alert">{error}</div>}

          {canSeeChecks && isAdmin && tab !== 'waiting' && (summaryMeta.people_without_role ?? 0) > 0 && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-amber-50 px-4 py-2.5 text-sm text-amber-900 ring-1 ring-amber-200">
              <span>
                {summaryMeta.people_without_role} {summaryMeta.people_without_role === 1 ? 'person who moved' : 'people who moved'} tickets in {fmt.month(period)} {summaryMeta.people_without_role === 1 ? 'has' : 'have'} no role, so their moves are not judged.
              </span>
              <Link to="/settings?tab=flow" className="rounded-lg px-2.5 py-1 text-xs font-semibold text-amber-900 ring-1 ring-inset ring-amber-300 transition-colors hover:bg-amber-100 active:scale-[0.97] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">
                Assign roles
              </Link>
            </div>
          )}

          {canSeeChecks && !isAdmin && summaryMeta.unlinked && (
            <div className="mb-4 rounded-xl bg-amber-50 px-4 py-2.5 text-sm text-amber-900 ring-1 ring-amber-200">
              Your login is not matched to a Jira account yet, so your own moves cannot be shown. Ask an admin to link it.
            </div>
          )}

          {/* Scores per project: the filter for the views below */}
          {canSeeChecks && tab !== 'waiting' && (
            loadingSummary && !summary ? (
              <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4" role="status" aria-label="Loading scores">
                {[0, 1, 2, 3].map((i) => <div key={i} className="h-32 animate-pulse rounded-2xl bg-white ring-1 ring-gray-200/70 motion-reduce:animate-none" />)}
              </div>
            ) : projects.length > 0 ? (
              <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                {projects.map((p) => (
                  <FlowScoreCard key={p.project_key} row={p} selected={projectKey === p.project_key} onSelect={changeProject} />
                ))}
              </div>
            ) : null
          )}

          {canSeeChecks && <FlowTabs tabs={tabs} active={tab} onChange={(id) => setTab(id as TabId)} />}

          {tab === 'handoffs' && canSeeChecks && (
            <div role="tabpanel" id="flow-panel-handoffs" aria-labelledby="flow-tab-handoffs">
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-sm font-semibold text-gray-900">
                  Moved outside the usual owner{projectKey ? ` · ${projectKey}` : ''}{total > 0 ? ` · ${fmt.count(total)} ${total === 1 ? 'move' : 'moves'}` : ''}
                </h2>
                <div className="text-xs text-gray-500">Grouped by who moved the ticket · hand it over with a comment instead</div>
              </div>
              <HandoffList rows={rows} people={people} loading={loadingRows && rows.length === 0} emptyText={emptyHandoffs} />
              {total > rows.length && (
                <div className="mt-3 text-center">
                  <button type="button" onClick={() => setLimit((n) => n + PAGE)} className="rounded-xl px-4 py-2 text-sm font-medium text-blue-700 transition-[background-color,transform] hover:bg-blue-50/70 active:scale-[0.98] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                    Show {Math.min(PAGE, total - rows.length)} more <span className="text-gray-500">· {total - rows.length} left</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {tab === 'rework' && canSeeChecks && (
            <div role="tabpanel" id="flow-panel-rework" aria-labelledby="flow-tab-rework">
              <ReworkView data={rework} loading={loadingRework} emptyText={`No ticket changed status in ${fmt.month(period)} within your scope.`} />
            </div>
          )}

          {tab === 'waiting' && (
            <div role={canSeeChecks ? 'tabpanel' : undefined} id="flow-panel-waiting" aria-labelledby={canSeeChecks ? 'flow-tab-waiting' : undefined}>
              {!canSeeChecks && (
                <div className="mb-3 flex items-center">
                  <h2 className="text-sm font-semibold text-gray-900">Where tickets wait</h2>
                  <InfoTip id="flow.waiting" />
                </div>
              )}
              <WaitingView period={period} projectKey={projectKey} onProject={changeProject} refreshKey={refreshKey} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
